import React from 'react';
import { motion } from 'framer-motion';
import { Play, Mic2, Music } from 'lucide-react';
import { Button } from './ui/Button';
import { VinylRecord } from './ui/VinylRecord';
import { Equalizer } from './ui/Equalizer';
export function Hero() {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-id="element-57">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-background bg-noise z-0" data-id="element-58" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-purple/20 rounded-full blur-[100px] animate-pulse" data-id="element-59" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-[100px] animate-pulse" style={{
      animationDelay: '1s'
    }} data-id="element-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-61">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-62">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="space-y-8" data-id="element-63">
            <div className="flex items-center gap-3 mb-4" data-id="element-64">
              <Equalizer count={3} height="h-8" color="bg-neon-amber" data-id="element-65" />
              <span className="text-neon-amber font-mono text-sm tracking-widest uppercase" data-id="element-66">
                Audio Sync Established
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-display font-black leading-tight text-white" data-id="element-67">
              UNLEASH THE <br data-id="element-68" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan to-neon-purple text-glow-cyan" data-id="element-69">
                RHYTHM
              </span>
              <br data-id="element-70" /> WITHIN
            </h1>

            <p className="text-xl text-gray-400 max-w-lg leading-relaxed" data-id="element-71">
              Lutegames bridges the gap between passive listening and active
              creation. Experience music gaming that syncs with your heartbeat.
            </p>

            <div className="flex flex-wrap gap-4 pt-4" data-id="element-72">
              <Button variant="primary" size="lg" glow data-id="element-73">
                <Play className="w-5 h-5 fill-current" data-id="element-74" />
                PLAY DEMO
              </Button>
              <Button variant="outline" size="lg" data-id="element-75">
                EXPLORE LIBRARY
              </Button>
            </div>

            <div className="flex items-center gap-8 pt-8 border-t border-white/10" data-id="element-76">
              <div className="flex items-center gap-2" data-id="element-77">
                <Music className="text-neon-magenta" data-id="element-78" />
                <span className="text-sm text-gray-400" data-id="element-79">
                  High-Fidelity Audio
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-80">
                <Mic2 className="text-neon-cyan" data-id="element-81" />
                <span className="text-sm text-gray-400" data-id="element-82">Voice Integration</span>
              </div>
            </div>
          </motion.div>

          {/* Visual Content */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8,
          rotate: -10
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative flex justify-center lg:justify-end" data-id="element-83">
            <div className="relative" data-id="element-84">
              {/* Decorative rings */}
              <div className="absolute inset-0 border border-white/10 rounded-full scale-150 animate-pulse" data-id="element-85" />
              <div className="absolute inset-0 border border-white/5 rounded-full scale-[1.8]" data-id="element-86" />

              <VinylRecord size="xl" labelColor="bg-neon-cyan" data-id="element-87" />

              {/* Floating Equalizers */}
              <div className="absolute -bottom-10 -left-10 bg-surface/80 backdrop-blur-md p-4 rounded-lg border border-white/10 shadow-2xl transform rotate-3" data-id="element-88">
                <Equalizer count={8} height="h-12" color="bg-neon-magenta" data-id="element-89" />
              </div>

              <div className="absolute -top-5 -right-5 bg-surface/80 backdrop-blur-md p-3 rounded-lg border border-white/10 shadow-2xl transform -rotate-6" data-id="element-90">
                <div className="text-xs text-gray-400 mb-1 font-mono" data-id="element-91">
                  BPM: 128
                </div>
                <div className="flex gap-1" data-id="element-92">
                  <div className="w-1 h-4 bg-neon-cyan animate-pulse" data-id="element-93"></div>
                  <div className="w-1 h-6 bg-neon-cyan animate-pulse delay-75" data-id="element-94"></div>
                  <div className="w-1 h-3 bg-neon-cyan animate-pulse delay-150" data-id="element-95"></div>
                  <div className="w-1 h-5 bg-neon-cyan animate-pulse delay-100" data-id="element-96"></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2" animate={{
      y: [0, 10, 0]
    }} transition={{
      repeat: Infinity,
      duration: 2
    }} data-id="element-97">
        <span className="text-xs text-gray-500 uppercase tracking-widest" data-id="element-98">
          Scroll to Sync
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-neon-cyan to-transparent" data-id="element-99" />
      </motion.div>
    </section>;
}